import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  PendencyHeatmapCellRaw,
  PendencyTeamMetricsRaw,
  SerializedPendencyRaw,
} from '../types';

export const TEAM_PENDENCIES_KEY = ['team-pendencies'] as const;

export function useTeamPendencies(params: { ownerUserId?: string; status?: string } = {}): {
  data: { data: SerializedPendencyRaw[] } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...TEAM_PENDENCIES_KEY, 'list', params],
    queryFn: () => api.listPendencies(params),
    staleTime: 30 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useTeamMetrics(): { data: PendencyTeamMetricsRaw | undefined; isLoading: boolean } {
  const q = useQuery({
    queryKey: [...TEAM_PENDENCIES_KEY, 'metrics'],
    queryFn: () => api.getTeamPendencyMetrics(),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading };
}

export function useTeamHeatmap(): {
  data: { data: PendencyHeatmapCellRaw[] } | undefined;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: [...TEAM_PENDENCIES_KEY, 'heatmap'],
    queryFn: () => api.getTeamPendencyHeatmap(),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading };
}

export function useBulkReassign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.bulkReassignPendencies>[0]) =>
      api.bulkReassignPendencies(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: TEAM_PENDENCIES_KEY }),
  });
}

export function useBulkPostpone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.bulkPostponePendencies>[0]) =>
      api.bulkPostponePendencies(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: TEAM_PENDENCIES_KEY }),
  });
}

export function useResolvePendency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      version,
      resolutionNote,
    }: {
      id: string;
      version: number;
      resolutionNote?: string;
    }) => api.resolvePendency(id, version, resolutionNote !== undefined ? { resolutionNote } : {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: TEAM_PENDENCIES_KEY }),
  });
}
